import type { Platform } from '~/utils/url-extractor.util';
import { extractPlatformFromUrl } from '~/utils/url-extractor.util';

export interface WorkspaceProjectLike {
  slug?: string;
  name?: string;
  [key: string]: unknown;
}

const PLATFORM_FIELD_MAP: Record<Platform, string> = {
  instagram: 'instagram_username',
  tiktok: 'tiktok_username',
  facebook: 'facebook_username',
  youtube: 'youtube_username'
};

export function extractFirstHttpUrl(input: string): string | null {
  const matches =
    input.match(/(?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/\S*)?/gi) || [];

  for (const rawMatch of matches) {
    const cleaned = rawMatch
      .trim()
      .replace(/[)\]}>,.!?;:]+$/g, '')
      .replace(/^<|>$/g, '');

    if (!cleaned) {
      continue;
    }

    const withProtocol = /^https?:\/\//i.test(cleaned) ? cleaned : `https://${cleaned}`;

    try {
      const parsed = new URL(withProtocol);
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        return withProtocol;
      }
    } catch {
      continue;
    }
  }

  return null;
}

export function listWorkspaceProjectOptions(projects: WorkspaceProjectLike[]) {
  return projects
    .filter(project => typeof project.slug === 'string' && project.slug.trim().length > 0)
    .map(project => ({
      label: project.name || project.slug || 'Unknown Project',
      value: project.slug as string
    }));
}

export function findMatchingProjectsByPlatform(
  projects: WorkspaceProjectLike[],
  platform: Platform
): Array<{ label: string; value: string }> {
  const platformField = PLATFORM_FIELD_MAP[platform];

  const matching = projects.filter(project => {
    const projectAny = project as Record<string, unknown>;
    const hasPlatform =
      projectAny[platformField] ||
      projectAny[platformField.replace('_username', 'Username')] ||
      projectAny[`${platform}Username`] ||
      projectAny[`${platform}_username`];

    return !!hasPlatform;
  });

  return listWorkspaceProjectOptions(matching);
}

export function resolveProjectSlugForUrlInput(params: {
  input: string;
  projects: WorkspaceProjectLike[];
  preferredProjectSlug?: string | null;
}): {
  extractedUrl: string | null;
  platform: Platform | null;
  matchingProjects: Array<{ label: string; value: string }>;
  projectSlug: string | null;
  errorCode?: 'NO_URL_FOUND' | 'NO_PROJECTS' | 'NO_PROJECT_FOR_PLATFORM';
} {
  const extractedUrl = extractFirstHttpUrl(params.input);
  if (!extractedUrl) {
    return {
      extractedUrl: null,
      platform: null,
      matchingProjects: [],
      projectSlug: null,
      errorCode: 'NO_URL_FOUND'
    };
  }

  const platform = extractPlatformFromUrl(extractedUrl);
  const allProjects = listWorkspaceProjectOptions(params.projects);
  const matchingProjects = platform
    ? findMatchingProjectsByPlatform(params.projects, platform)
    : allProjects;

  if (allProjects.length === 0) {
    return {
      extractedUrl,
      platform,
      matchingProjects,
      projectSlug: null,
      errorCode: 'NO_PROJECTS'
    };
  }

  if (platform && matchingProjects.length === 0) {
    return {
      extractedUrl,
      platform,
      matchingProjects,
      projectSlug: null,
      errorCode: 'NO_PROJECT_FOR_PLATFORM'
    };
  }

  const preferred = params.preferredProjectSlug?.trim();
  const preferredInMatches = preferred
    ? matchingProjects.find(option => option.value === preferred)?.value
    : null;

  return {
    extractedUrl,
    platform,
    matchingProjects,
    projectSlug: preferredInMatches || matchingProjects[0]?.value || null
  };
}
